import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Clock, Check, Globe } from '@phosphor-icons/react';
import { Switch } from './ui/switch';
import { refreshPlatformConfig } from '../utils/platformConfig';
import { ExpirationPreview } from './admin/ExpirationPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CUSTOM_OPTION = 'custom';

const isValidCustomDuration = (raw, min, max) => {
  if (raw === '' || raw === null || raw === undefined) return false;
  // Reject decimals via regex BEFORE Number() so "1.5" is caught.
  if (!/^\d+$/.test(String(raw).trim())) return false;
  const n = Number(raw);
  return Number.isInteger(n) && n >= min && n <= max;
};

export const ListingLifecycleManagement = () => {
  const [current, setCurrent] = useState(null);
  const [allowed, setAllowed] = useState([]);
  const [minCustom, setMinCustom] = useState(1);
  const [maxCustom, setMaxCustom] = useState(365);
  const [lastCustom, setLastCustom] = useState(null);

  // Selected option in the dropdown: a number string for predefined values
  // or the literal "custom" sentinel.
  const [selectedOption, setSelectedOption] = useState('');
  // Raw text from the custom input (kept as string so we can validate).
  const [customRaw, setCustomRaw] = useState('');
  const [customError, setCustomError] = useState('');

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [translateSaving, setTranslateSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/platform-config`, { withCredentials: true });
        setCurrent(data.listing_duration_days);
        setAllowed(data.allowed_durations || []);
        setMinCustom(data.min_custom_duration || 1);
        setMaxCustom(data.max_custom_duration || 365);
        setLastCustom(data.last_custom_duration_days || null);
        setAutoTranslate(data.auto_translate_enabled !== false);

        const isPredefined = (data.allowed_durations || []).includes(data.listing_duration_days);
        if (isPredefined) {
          setSelectedOption(String(data.listing_duration_days));
        } else {
          // Active duration is custom: surface it in the input, select Custom.
          setSelectedOption(CUSTOM_OPTION);
          setCustomRaw(String(data.listing_duration_days));
        }
      } catch {
        toast.error('Failed to load platform config');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleAutoTranslate = async (checked) => {
    setAutoTranslate(checked);  // optimistic
    setTranslateSaving(true);
    try {
      await axios.put(
        `${API}/admin/platform-config`,
        { auto_translate_enabled: checked },
        { withCredentials: true },
      );
      toast.success(checked ? 'Automatic translation enabled' : 'Automatic translation disabled');
    } catch (err) {
      setAutoTranslate(!checked);  // revert
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setTranslateSaving(false);
    }
  };

  // Pre-fill the custom input the first time the user picks "Custom"
  // if there's a remembered last-custom value.
  const handleSelectChange = (v) => {
    setSelectedOption(v);
    setCustomError('');
    if (v === CUSTOM_OPTION && !customRaw && lastCustom) {
      setCustomRaw(String(lastCustom));
    }
  };

  const handleCustomChange = (e) => {
    const val = e.target.value;
    setCustomRaw(val);
    if (val === '') {
      setCustomError('');
      return;
    }
    if (!isValidCustomDuration(val, minCustom, maxCustom)) {
      setCustomError(`Enter a whole number between ${minCustom} and ${maxCustom}`);
    } else {
      setCustomError('');
    }
  };

  // The numeric value the admin is about to save.
  const resolveTargetDays = () => {
    if (selectedOption === CUSTOM_OPTION) {
      if (!isValidCustomDuration(customRaw, minCustom, maxCustom)) return null;
      return Number(customRaw);
    }
    if (selectedOption === '' || selectedOption == null) return null;
    return Number(selectedOption);
  };

  const targetDays = resolveTargetDays();
  const dirty = targetDays !== null && targetDays !== current;

  const handleSave = async () => {
    if (!dirty) return;
    if (selectedOption === CUSTOM_OPTION) {
      if (customRaw === '') {
        setCustomError('Custom duration is required');
        return;
      }
      if (!isValidCustomDuration(customRaw, minCustom, maxCustom)) {
        setCustomError(`Enter a whole number between ${minCustom} and ${maxCustom}`);
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/platform-config`,
        { listing_duration_days: targetDays },
        { withCredentials: true }
      );
      setCurrent(data.listing_duration_days);
      setLastCustom(data.last_custom_duration_days || null);
      refreshPlatformConfig();
      toast.success(`Listing duration updated to ${data.listing_duration_days} days`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const isPredefined = allowed.includes(current);
    if (isPredefined) {
      setSelectedOption(String(current));
      setCustomRaw('');
    } else {
      setSelectedOption(CUSTOM_OPTION);
      setCustomRaw(String(current));
    }
    setCustomError('');
  };

  if (loading) {
    return <div className="text-text_muted text-sm">Loading…</div>;
  }

  return (
    <div data-testid="listing-lifecycle-management">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-text_main mb-1">Listing Lifecycle</h2>
        <p className="text-sm text-text_muted">
          Controls how long new listings stay active before expiring. Applies to future listings and renewals.
        </p>
      </div>

      <div className="bg-white border border-border rounded-xl p-6 max-w-xl">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-full bg-warning-soft border border-warning flex items-center justify-center">
            <Clock size={18} weight="bold" className="text-warning" />
          </div>
          <div>
            <p className="text-sm font-semibold text-text_main">Listing duration</p>
            <p className="text-xs text-text_muted">
              Current: <span className="font-semibold text-text_main" data-testid="current-duration">{current} days</span>
            </p>
          </div>
        </div>

        <label className="block text-xs font-medium text-text_muted mb-2">Select new duration</label>
        <Select value={selectedOption} onValueChange={handleSelectChange}>
          <SelectTrigger className="w-full border border-border" data-testid="duration-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allowed.map((d) => (
              <SelectItem key={d} value={String(d)} data-testid={`duration-option-${d}`}>
                {d} days
              </SelectItem>
            ))}
            <SelectItem value={CUSTOM_OPTION} data-testid="duration-option-custom">
              Custom
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Preview for predefined values lives directly under the dropdown. */}
        {selectedOption !== CUSTOM_OPTION && selectedOption !== '' && (
          <ExpirationPreview days={Number(selectedOption)} testId="expiration-preview-predefined" />
        )}

        {selectedOption === CUSTOM_OPTION && (
          <div className="mt-4" data-testid="custom-duration-block">
            <label
              htmlFor="custom-duration-input"
              className="block text-xs font-medium text-text_muted mb-2"
            >
              Custom duration (days)
            </label>
            <Input
              id="custom-duration-input"
              type="number"
              inputMode="numeric"
              min={minCustom}
              max={maxCustom}
              step="1"
              value={customRaw}
              onChange={handleCustomChange}
              placeholder={`e.g. 21 (between ${minCustom} and ${maxCustom})`}
              className={`border ${customError ? 'border-danger focus-visible:ring-red-400' : 'border-border'}`}
              data-testid="custom-duration-input"
            />
            {customError && (
              <p className="mt-1.5 text-xs text-danger" data-testid="custom-duration-error">
                {customError}
              </p>
            )}
            {!customError && lastCustom && customRaw === '' && (
              <p className="mt-1.5 text-xs text-text_muted" data-testid="custom-duration-hint">
                Last custom value: <span className="font-medium">{lastCustom} days</span>
              </p>
            )}
            {/* Live calendar-date preview as the admin types a custom value. */}
            {!customError && isValidCustomDuration(customRaw, minCustom, maxCustom) && (
              <ExpirationPreview days={Number(customRaw)} testId="expiration-preview-custom" />
            )}
          </div>
        )}

        <div className="mt-5 flex items-center gap-3">
          <Button
            onClick={handleSave}
            disabled={!dirty || saving || !!customError}
            className="bg-primary hover:bg-primary-hover text-white disabled:opacity-50"
            data-testid="save-duration-btn"
          >
            {saving ? 'Saving…' : (
              <>
                <Check size={16} weight="bold" className="mr-1.5" />
                Save changes
              </>
            )}
          </Button>
          {dirty && (
            <button
              onClick={handleCancel}
              className="text-sm text-text_muted hover:text-text_main"
              data-testid="cancel-duration-btn"
            >
              Cancel
            </button>
          )}
        </div>

        <div className="mt-5 pt-4 border-t border-border/60 text-xs text-text_muted leading-relaxed">
          <p className="font-semibold text-text_main mb-1">How this works</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Applies instantly to new listings and 1-click reposts.</li>
            <li>Existing active listings keep their current expires_at.</li>
            <li>Reminder emails still fire at 7 days and 1 day before expiry.</li>
          </ul>
        </div>
      </div>

      {/* ─── Automatic translation toggle (FR → EN / MG) ─── */}
      <div className="bg-white border border-border rounded-xl p-6 max-w-xl mt-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-info-soft border border-info flex items-center justify-center mt-0.5">
              <Globe size={18} weight="bold" className="text-info" />
            </div>
            <div>
              <p className="text-sm font-semibold text-text_main">Automatic translation</p>
              <p className="text-xs text-text_muted mt-0.5">
                When ON, footer pages are translated from French to English/Malagasy on demand and cached. Admin edits override translations.
              </p>
            </div>
          </div>
          <Switch
            checked={autoTranslate}
            onCheckedChange={toggleAutoTranslate}
            disabled={translateSaving}
            data-testid="auto-translate-toggle"
            className="mt-1"
          />
        </div>
      </div>
    </div>
  );
};
